import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ToggleLeft, ToggleRight, Info } from 'lucide-react';

/**
 * Feature flag registry. Add a flag here with a label + description and it
 * automatically appears in the dev banner's Feature Flags panel. Flags persist
 * to localStorage (per-browser) so they survive refreshes — handy for demoing
 * or QA-ing an in-progress feature without a backend deploy.
 *
 * Read a flag in any component with: const on = useFeatureFlag('flagKey');
 */
export const FEATURE_FLAG_REGISTRY = [
  {
    key: 'newUI',
    label: 'New UI shell',
    description: 'Opt into redesigned navigation / layout while it is in progress.',
    default: false,
  },
  {
    key: 'betaFeature',
    label: 'Beta features',
    description: 'Generic gate for features that are dev-complete but not yet released.',
    default: false,
  },
  {
    key: 'experimentalFeature',
    label: 'Experimental',
    description: 'Unstable / spike work. May break — for internal evaluation only.',
    default: false,
  },
  {
    key: 'anotherFeature',
    label: 'Misc toggle',
    description: 'Placeholder slot for ad-hoc toggling during development.',
    default: false,
  },
];

const FLAGS_STORAGE_KEY = 'feature_flags';

const buildInitialFlags = () => {
  const defaults = Object.fromEntries(FEATURE_FLAG_REGISTRY.map((f) => [f.key, f.default]));
  try {
    const stored = JSON.parse(localStorage.getItem(FLAGS_STORAGE_KEY)) || {};
    // Registry defines the shape; stored values only override known keys.
    return { ...defaults, ...Object.fromEntries(Object.entries(stored).filter(([k]) => k in defaults)) };
  } catch {
    return defaults;
  }
};

const FeatureFlagsContext = React.createContext();

const FeatureFlags = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState(buildInitialFlags);
  const location = useLocation();

  // Persist on every change so flags survive a refresh.
  useEffect(() => {
    try {
      localStorage.setItem(FLAGS_STORAGE_KEY, JSON.stringify(featureFlags));
    } catch {
      /* ignore storage failures */
    }
  }, [featureFlags]);

  const toggleFeature = (feature) => {
    setFeatureFlags(prevFlags => ({
      ...prevFlags,
      [feature]: !prevFlags[feature],
    }));
  };

  const resetFeatureFlags = () => {
    setFeatureFlags(Object.fromEntries(FEATURE_FLAG_REGISTRY.map((f) => [f.key, f.default])));
  };

  return (
    <FeatureFlagsContext.Provider value={{ featureFlags, toggleFeature, resetFeatureFlags, registry: FEATURE_FLAG_REGISTRY }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

const FeatureFlagToggle = ({ feature, isEnabled, onToggle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex min-w-0 items-center gap-3">
        <Info className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
        <span className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">{feature}</span>
      </div>
      <button
        onClick={() => onToggle(feature)}
        className={`rounded-lg p-1.5 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${
          isEnabled ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400' : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
        }`}
      >
        {isEnabled ? (
          <ToggleRight className="h-6 w-6" />
        ) : (
          <ToggleLeft className="h-6 w-6" />
        )}
      </button>
    </motion.div>
  );
};

const FeatureFlagsManager = () => {
  const { featureFlags, toggleFeature } = React.useContext(FeatureFlagsContext);

  return (
    <div className="space-y-3">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Feature Flags</h2>
      <AnimatePresence>
        {Object.entries(featureFlags).map(([feature, isEnabled]) => (
          <FeatureFlagToggle
            key={feature}
            feature={feature}
            isEnabled={isEnabled}
            onToggle={toggleFeature}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

export { FeatureFlags, FeatureFlagsContext, FeatureFlagsManager };